// Client for the admin fragment inventory (/api/fragments on BCC.ADMIN.API): the read-only list
// of every fragment the system can bake + its latest publishing per environment.
//
// IMPORTANT: goes through the shared `axios` instance (NOT raw fetch) so the Auth0AxiosBridge
// request interceptor attaches the admin Bearer token : a raw fetch is invisible to that
// interceptor and 401s when BYPASS_AUTH is off.
import axios from "axios";

export interface FragmentEnvState {
	published: boolean;
	hash?: string | null;
	updatedAt?: string | null;
}

export interface FragmentInventoryItem {
	key: string;
	kind: "block" | "shared" | "record" | "orphan" | string;
	title: string;
	description?: string | null;
	dependsOn: string[];
	preview: FragmentEnvState;
	production: FragmentEnvState;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export async function listFragments(): Promise<FragmentInventoryItem[]> {
	const r = await axios.get<FragmentInventoryItem[]>(`${apiBase}/api/fragments`, { withCredentials: true });
	return Array.isArray(r.data) ? r.data : [];
}

/**
 * Fetch one baked fragment's content (the exact JSON the public site reads from the CDN). Used by the
 * page-editor preview to render computed widgets : MarketsBoard cards, KeyIndicatorsBar, InformationHub,
 * FxRatesExplorer, governors : from the SAME fragment the live site shows, so the preview matches what
 * will deploy instead of re-projecting per widget client-side. Returns null when the fragment has not
 * been baked for that environment yet (the widget then renders its "déployé" placeholder, same as a miss
 * on the live site).
 */
export async function getFragmentContent(
	key: string,
	environment: "preview" | "production" = "preview",
	language = "fr"
): Promise<unknown | null> {
	try {
		const r = await axios.get(`${apiBase}/api/fragments/${encodeURIComponent(key)}/content`, {
			params: { environment, language },
			withCredentials: true
		});
		return r.data ?? null;
	} catch {
		return null; // NO FALLBACK : a miss renders nothing, exactly like the live site
	}
}
